<template>
  <div
    v-if="showReport"
    id="reportPrintContainer"
  >
    <div
      id="reportContainer"
      ref="reportContainer"
      :style="reportStyle.table + reportStyleOptions"
      :class="groupHeaderOffsetClass"
    >
      <!--
        NOTHING BELOW HERE IS REACTIVE! IT WILL ONLY RENDER ONCE!
        We must use v-once here, else the report table will get re-rendered far too often.
        With v-once, plus an incrementing :key in the parent component,
        this will only be rendered once for each report run, regardless of what values change in its ancestors.
      -->
      <table
        v-once
        ref="reportTable"
        class="ft-report-table"
      >
        <!-- REPORT CAPTION -->
        <caption>
          {{ $ftUser.clubName }} {{ title }}
          <p
            v-if="subTitle"
            class="ft-report-subtitle"
          >
            {{ subTitle }}
          </p>
        </caption>
        <!-- REPORT HEADERS -->
        <thead
          ref="reportHeader"
          v-resize="setReportHeaderHeight"
        >
          <tr>
            <template v-for="(header, i) in headers">
              <th
                v-if="header.html"
                :key="i+'1_rhi'"
                :class="header.thClass"
                v-html="header.html"
              />
              <th
                v-else-if="header.text"
                :key="i+'2_rhi'"
                :class="header.thClass"
              >
                {{ header.text }}
              </th>
              <th
                v-else
                :key="i+'3_rhi'"
                :class="header.thClass"
              >
                <slot
                  :name="'th-' + header.field"
                  :index="i"
                  :item="header"
                />
              </th>
            </template>
          </tr>
        </thead>
        <!-- REPORT/GROUP BODY -->
        <tbody
          v-for="(group, gi) in groupedData"
          :key="gi + '_group'"
          :class="group.class"
        >
          <template v-for="(row, ri) in group.rows">
            <!-- GROUP CAPTION -->
            <tr
              v-if="hasCaption(row.__groupAction)"
              :key="ri+'_groupcap'"
              :class="getGroupCaptionClass(row, ri, group.rows, group)"
            >
              <td
                :colspan="headers.length"
              >
                {{ row.__groupAction.caption(row, ri, group.rows) }}
              </td>
            </tr>
            <!-- REPORT/GROUP ROWS -->
            <tr
              :key="ri+'_group_row'"
              :class="getRowClass(row, ri, group.rows, group)"
            >
              <!-- REPORT ROW -->
              <td
                v-for="(header, hi) in headers.filter((h, i) => !skipCell(h, row, ri, i, group.rows, group))"
                :key="hi+'_'+ri+'_rr'"
                :rowspan="rowSpan(header, row, ri, hi, group.rows, group)"
                :class="getCellClass(header, row, ri, hi, group.rows, group)"
              >
                <template v-if="!header.slot">
                  <template v-if="header.displayHtml">
                    <div v-html="header.displayHtml(row, ri, hi, group.rows, group, header)" />
                  </template>
                  <template v-else-if="header.type">
                    <span
                      v-if="header.type === 'DATE_TIME'"
                      class="ft-date ft-time"
                    >
                      <span>{{ displayDate(header.displayCell ? header.displayCell(row, ri, hi, group.rows, group, header) : row[header.field]) }}</span>
                      <span>{{ displayTime(header.displayCell ? header.displayCell(row, ri, hi, group.rows, group, header) : row[header.field]) }}</span>
                    </span>
                    <span
                      v-else-if="header.type === 'DATE'"
                      class="ft-date"
                    >
                      <span style="white-space: nowrap;">{{ displayDate(header.displayCell ? header.displayCell(row, ri, hi, group.rows, group, header) : row[header.field]) }}</span>
                    </span>
                    <span
                      v-else-if="header.type === 'TIME'"
                      class="ft-time"
                    >
                      <span>{{ displayTime(header.displayCell ? header.displayCell(row, ri, hi, group.rows, group, header) : row[header.field]) }}</span>
                    </span>
                    <span
                      v-else-if="header.type === 'BOOLEAN'"
                      class="ft-boolean"
                    >
                      <span>{{ (header.displayCell ? header.displayCell(row, ri, hi, group.rows, group, header) : row[header.field]) ? 'True' : 'False' }}</span>
                    </span>
                    <span
                      v-else-if="header.type === 'FLAG'"
                      class="ft-boolean"
                    >
                      <!-- <span>{{ (row[header.field] === undefined || row[header.field] === null || row[header.field] === false) ? '' : '<v-icon>check</v-icon>' }}</span> -->
                      <span v-if="header.displayCell ? header.displayCell(row, ri, hi, group.rows, group, header) : row[header.field]"><v-icon>check</v-icon></span>
                    </span>
                  </template>
                  <template v-else-if="header.displayCell">
                    {{ header.displayCell(row, ri, hi, group.rows, group, header) }}
                  </template>
                  <template v-else>
                    {{ row[header.field] }}
                  </template>
                </template>
                <slot
                  :name="'td-' + (header.slotName || header.field)"
                  :header="header"
                  :index="hi"
                  :row="row"
                  :row-index="ri"
                  :item="row[header.field]"
                  :rows="group.rows"
                  :group="group"
                />
              </td>
            </tr>
            <slot
              name="row"
              :headers="headers"
              :row="row"
              :index="ri"
              :rows="group.rows"
              :group="group"
            />
            <!-- GROUP / REPORT TOTALS -->
            <template v-if="row.__groupAction && row.__groupAction.aggregators">
              <template v-for="(aggregator, ai) in row.__groupAction.aggregators">
                <template v-if="aggregator.grouping && aggregator.grouping.emptyRows">
                  <tr
                    v-for="bli in aggregator.grouping.emptyRows"
                    :key="'bli_' + bli + '_' + gi + '_' + ri"
                    class="ft-report-empty-row"
                    :class="'ft-empty-row-' + bli"
                  >
                    <td
                      v-for="(header, hi) in headers"
                      :key="bli + '_' + gi +'_' + hi + '_grt'"
                    >
&nbsp;
                    </td>
                  </tr>
                </template>
                <tr
                  :key="'ga_'+ ri + '_' + ai + '_' + gi"
                  :class="getGroupTotalClass(aggregator, row.__groupAction.aggregators, ai, row, ri, group.rows, group)"
                >
                  <td
                    v-for="(header, hi) in headers.filter(h => useAggregateHeader(h))"
                    :key="'ga_'+ ri + '_' + ai + '_' + gi + '_' + hi"
                    :colspan="getAggregateSpan(header)"
                  >
                    <span
                      v-if="hi === 0 && !header.aggregate && aggregator.grouping.aggregateLabel"
                      class="ft-aggregate-label"
                    >{{ getAggregateLabel(aggregator, row, ri, group.rows) }}</span>
                    <template v-else-if="header.aggregate && !header.aggregateSlot">
                      <template v-if="header.displayAggregate">
                        {{ header.displayAggregate(aggregator.aggregates[header.field], row, ri, group.rows, header) }}
                      </template>
                      <template v-else>
                        {{ aggregator.aggregates[header.field] }}
                      </template>
                    </template>
                    <slot
                      :name="'aggregate-' + (header.slotName || header.field)"
                      :aggregator="aggregator"
                      :aggregates="aggregator.aggregates"
                      :aggregate="aggregator.aggregates[header.field]"
                      :display="header.displayAggregate && header.displayAggregate(aggregator.aggregates[header.field], row, ri, group.rows)"
                      :headers="headers"
                      :row="row"
                      :index="ri"
                      :rows="group.rows"
                      :group="group"
                    />
                  </td>
                </tr>
              </template>
            </template>
          </template>
        </tbody>

        <!-- REPORT SUMMARY -->
        <tbody
          v-if="showSummary"
          class="ft-summary"
        >
          <!-- SUMMARY CAPTION -->
          <tr
            v-if="summaryCaptionSlot || summaryCaption"
            :class="getSummaryCaptionClass()"
          >
            <td :colspan="headers.length">
              <template v-if="summaryCaption">
                {{ summaryCaption }}
              </template>
              <slot name="summary-caption" />
            </td>
          </tr>
          <tr :class="getSummaryBodyClass()">
            <td :colspan="headers.length">
              <slot name="summary-body" />
            </td>
          </tr>
        </tbody>

        <!-- REPORT FOOTER -->
        <tfoot>
          <tr>
            <td :colspan="headers.length">
              <p v-if="reportDateTime">
                Report generated on {{ reportDateTime.format('MMMM Do YYYY [at] h:mm:ss A') }} by {{ $ftUser.name }}
                <span
                  v-if="reportName"
                  class="reportFooterSavedReport"
                >using saved report '{{ reportName }}'</span>
              </p>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import NavRoute from '../../mixins/navRoute'
import fixedBarModal from '../../mixins/fixedBarModal'
import exporting from './mixins/exporting'
import {displayTime, displayDate} from '../../util/helpers'

export default {
  name: 'ReportTable',
  mixins: [NavRoute, fixedBarModal, exporting],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    reportName: {
      type: String,
      default: ''
    },
    scale: {
      type: Number,
      default: 0
    },
    reportData: {
      type: Array,
      default: () => []
    },
    reportDateTime: {
      type: Object,
      default: undefined
    },
    title: String,
    subTitle: String,
    summaryCaption: String,
    summaryCaptionSlot: Boolean,
    summaryCaptionClass: [String, Object, Array],
    summaryBodyClass: [String, Object, Array],
    showSummary: Boolean,
    headers: {
      type: Array,
      default: () => []
    },
    grouped: Boolean,
    // checkIcon: '<v-icon>check</v-icon>',
    rowClass: {
      type: Function,
      default () {}
    },
    defaultFontPercent: {
      type: Number,
      default: 100
    }
  },
  data: function () {
    return {
      showReport: false,
      reportHeaderHeight: 40 // dynamically updates
    }
  },
  computed: {
    groupedData () {
      return this.grouped ? this.reportData : [{rows: this.reportData}]
    },
    reportStyle () {
      return this.$ftStyle.report
    },
    groupHeaderOffsetClass () {
      return 'ft-group-header-' + this.reportHeaderHeight
    },
    reportStyleOptions () {
      var result = ' font-size: ' + this.defaultFontPercent * (this.scale || 1) + '%;'
      return result
    }
  },
  watch: {
    scale (v) {
      this.setReportHeaderHeight()
    },
    value (v) {
      if (v !== this.showReport) this.showReport = v
    },
    showReport (val) {
      if (val) {
        this.addBodyPrintClass()
      } else {
        this.removeBodyPrintClass()
      }
      this.$emit('input', val)
    }
  },
  mounted () {
    this.showReport = this.value
  },
  beforeDestroy () {
    this.removeBodyPrintClass()
  },
  methods: {
    displayTime,
    displayDate,
    hasCaption (groupAction) {
      return groupAction && typeof groupAction.caption === 'function'
    },
    getAggregateLabel (aggregator, row, rowIndex, rows) {
      const aggregateLabel = aggregator.grouping.aggregateLabel
      if (typeof aggregateLabel === 'function') return aggregateLabel(row, rowIndex, rows)
      return aggregateLabel
    },
    rowSpan (header, row, rowIndex, cellIndex, rows) {
      if (!header.spanGroup) return
      return rows.length
    },
    useAggregateHeader (header) {
      return header.aggregate || header.__aggregateSpan
    },
    getAggregateSpan (header) {
      if (header.__aggregateSpan) return header.__aggregateSpan
    },
    skipCell (header, row, rowIndex, cellIndex, rows) {
      if (!header.spanGroup) return false
      return rowIndex !== 0
    },
    getSummaryBodyClass () {
      const cls = []
      const captionClass = this.summaryBodyClass
      if (captionClass) {
        cls.push(...Array.isArray(captionClass) ? captionClass : [captionClass])
      }
      cls.push('ft-summary-body')
      return cls
    },
    getSummaryCaptionClass () {
      const cls = []
      const captionClass = this.summaryCaptionClass
      if (captionClass) {
        cls.push(...Array.isArray(captionClass) ? captionClass : [captionClass])
      }
      cls.push('ft-group-caption ft-summary-caption')
      return cls
    },
    getGroupCaptionClass (row, rowIndex, rows, group) {
      const cls = []
      const groupAction = row.__groupAction
      const captionClass = groupAction.captionClass
      if (captionClass) {
        cls.push(...Array.isArray(captionClass) ? captionClass : [captionClass])
      }
      cls.push('ft-group-caption')
      return cls
    },
    getGroupTotalClass (aggregator, aggregators, aggregatorIndex, row, rowIndex, rows, group) {
      const cls = []
      const grouping = aggregator.grouping
      const aggregateRowClass = grouping.aggregateRowClass
      if (aggregateRowClass) {
        cls.push(...Array.isArray(aggregateRowClass) ? aggregateRowClass : [aggregateRowClass])
      }
      cls.push('ft-aggregate')
      if (grouping.thinAggregate) cls.push('ft-thin-aggregate')
      if (aggregatorIndex >= aggregators.length - 1) cls.push('ft-aggregate-last')
      return cls
    },
    getRowClass (row, rowIndex, rows, group) {
      const cls = []
      const rowClass = this.rowClass(row, rowIndex, rows, group)
      if (rowClass) {
        cls.push(...Array.isArray(rowClass) ? rowClass : [rowClass])
      }
      const groupAction = row.__groupAction
      if (groupAction && groupAction.showSeparator) {
        cls.push('ft-separator')
      }
      cls.push('ft-report-row')
      return cls
    },
    getCellClass (header, row, rowIndex, cellIndex, rows) {
      const cellClass = header.cellClass
      if (!cellClass) return
      if (typeof cellClass === 'function') {
        return cellClass(row, rowIndex, cellIndex, rows)
      } else {
        return cellClass
      }
    },
    setReportHeaderHeight (finalRun) {
      if (this.showReport && this.$refs && this.$refs.reportHeader) {
        this.reportHeaderHeight = this.$refs.reportHeader.offsetHeight
        if (finalRun !== true) {
          // Check again a few times to make sure we get it
          setTimeout(() => {this.setReportHeaderHeight(true)}, 250)
          setTimeout(() => {this.setReportHeaderHeight(true)}, 500)
          setTimeout(() => {this.setReportHeaderHeight(true)}, 1000)
        }
      }
      return true
    },
    addBodyPrintClass () {
      const root = document.getElementsByTagName('html')[0]
      root.classList.add('ft-report-active')
    },
    removeBodyPrintClass () {
      const root = document.getElementsByTagName('html')[0]
      root.classList.remove('ft-report-active')
    }
  }
}
</script>

<style>
@media print {
  @page {
    margin-left: 6.35mm; /* Shrink margins */
    margin-right: 6.35mm; /* Shrink margins */
  }
  /* Prepend all print selectors with .ft-report-active, that way this print rule will only apply when a report is open */
  .ft-report-active .toolbar {
    display: none;
  }
  .ft-report-active .v-application,
  .ft-report-active .v-dialog__content__active,
  .ft-report-active .v-dialog,
  .ft-report-active .v-card {
    display: block !important; /* Needed for FireFox to print more than one page*/
    visibility: hidden;
  }
  .ft-report-active body *,
  .ft-report-active .v-dialog,
  .ft-report-active .v-dialog__content,
  .ft-report-active .v-card__text,
  .ft-report-active .v-dialog--fullscreen,
  .ft-report-active .v-toolbar {
    visibility: hidden;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }
  .ft-report-active body,
  .ft-report-active .v-dialog,
  .ft-report-active .v-card__text,
  .ft-report-active .v-dialog--scrollable > .v-card > .v-card__text,
  .ft-report-active .v-dialog--fullscreen {
    overflow: visible;
    overflow-y: visible;
  }
  .ft-report-active #reportContainer { backface-visibility: visible; visibility: visible; position: absolute; top: 0; left: 0; right: 0;}
  .ft-report-active #reportContainer * { backface-visibility: visible; visibility: visible; position: static; }
  .ft-report-active table.ft-report-table caption { margin-top: 0 !important; }
  .ft-report-active #reportVCard {
    position: relative;
  }
  .ft-report-active table.ft-report-table thead {
    border-top: 1px solid white; /* Fix for thead color bleed through to previous page footer issue on chrome */
    page-break-inside:avoid !important;
    -webkit-region-break-inside: avoid !important;
    display:table-header-group;
  }
  .ft-report-active table.ft-report-table tfoot {
    page-break-inside:avoid !important;
    -webkit-region-break-inside: avoid !important;
    /* display:table-footer-group; */
    display:table-row-group;
  }
  .ft-report-active table.ft-report-table tr {
    page-break-inside:avoid !important;
    -webkit-region-break-inside: avoid !important;
  }
  /*
  .ft-report-active table.ft-report-table tbody.ft-report-flat-group {
    page-break-inside:avoid !important;
    -webkit-region-break-inside: avoid !important;
  }
  */
}

/* DEFAULT FONT SIZE */
#reportContainer {
  /* Override any theme colors */
  background: white;
  color: black;
  /* Make sure we use a consistent initial font size */
  font-size: 14px; /* px is okay here */
  line-height: normal;
  font-weight: normal;
}

/* TABLE DEFAULTS */
table.ft-report-table {
  width: 100%;
  text-align: left;
  border-collapse: collapse;
}

/* ALL sizes from here on should be in em! (this allows scaling to work.  Don't get tempted to use PX unless otherwise noted!) */

/* MAIN REPORT CAPTION */
table.ft-report-table caption {
  font-size: 1.5em;
  font-weight: bold;
  margin-top: .9em;
  margin-bottom: .9em;
}
table.ft-report-table caption .ft-report-subtitle {
  font-size: 90%;
  padding-bottom: 0;
  margin-bottom: 0;
}

/* HEADERS ROWS & GROUP CAPTION HEADER */
table.ft-report-table tbody tr.ft-group-caption td,
table.ft-report-table thead tr th {
  font-size: 1.01em;
  font-weight: bold;
  color: #FFFFFF;
  background: #1C6EA4;
  text-align: left;
  position: sticky; /* IE11 will just have to suffer, if we can get it working with this app at all. */
  top: 0;
}
/* ROW GROUP CAPTIONS */
table.ft-report-table tbody tr.ft-group-caption td {
  text-align: center;
  top: 30px; /* PX okay here.  this is overridden by script and effectively 0 when printing */
}
@supports (-webkit-marquee-repetition:infinite) and (object-fit:fill) {
  /* Target Safari with CSS hack */
  /* Safari's position sticky is broken is some cases, so until we figure out how to fix it, disable it */
  table.ft-report-table tbody tr.ft-group-caption td {
    position: static !important;
  }
  table.ft-report-table thead tr th {
    position: static !important;
  }
}

/* DEFAULT TBODY */
table.ft-report-table tbody td {
  font-size: .9em;
  vertical-align: top;
}
table.ft-report-table tbody td .ft-date.ft-time > span:first-child {
  margin-right: .5em;
}
table.ft-report-table tbody td:nth-last-child(n+2) {
  border-right: 1px solid rgb(207, 207, 207); /* PX okay for thin borders */
}

/* DEFAULT ROWS */
table.ft-report-table tbody:first-of-type tr.ft-separator:nth-child(n+2),
table.ft-report-table tbody:nth-of-type(n+2) tr.ft-separator {
  border-top: 1px solid rgb(181, 198, 211); /* PX okay for thin borders */
}
table.ft-report-table tbody td, table.ft-report-table thead th {
  padding: .4em .4em;
}

/* GROUPED ROWS */
table.ft-report-table tbody.ft-report-flat-group:nth-child(even),
table.ft-report-table tbody:not(.ft-report-flat-group) tr:nth-child(even) {
  background: rgb(236, 244, 253);
}
table.ft-report-table tbody.ft-report-flat-group tr:nth-child(even) td {
  background: rgba(163, 163, 163, 0.09);
}

/* AGGREGATES & SUMMARIES */
table.ft-report-table tbody tr.ft-aggregate {
  border-top: 1px solid rgb(181, 198, 211); /* PX okay for thin borders */
}
table.ft-report-table tbody tr.ft-thin-aggregate.ft-aggregate-last {
  border-bottom: 1px solid rgb(181, 198, 211); /* PX okay for thin borders */
}
table.ft-report-table tbody tr.ft-aggregate,
table.ft-report-table tbody tr.summary-body {
  background: rgb(255, 255, 255);
}
table.ft-report-table tbody tr.ft-aggregate-last:not(.ft-thin-aggregate) td {
  padding-bottom: 2em;
}
table.ft-report-table tbody tr.ft-aggregate td {
  border-right: none;
  background: none !important;
}

/* FOOTER */
table.ft-report-table tfoot {
  border-width: 0;
  padding: 0;
  margin: 0;
}
table.ft-report-table tfoot td {
  font-size: .8em;
  font-weight: normal;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
}
table.ft-report-table tfoot td p {
  padding-top: 1em;
}

/* INLINE TEXT BLOCK */
.ft-itb {
  white-space: nowrap;
  display: inline-block;
  background-color: rgba(46, 136, 58, 0.164);
  border-radius: 1em;
  margin: .1em .0;
  padding: .2em .5em;
  margin-right: .3em;
}
.ft-itb .ft-itb-sep {
  display: none;
}
.ft-itb .ft-itb-text {
  display:inline-block;
}
.ft-itb .ft-itb-value {
  display:inline-block;
  background-color: rgba(46, 136, 58, 0.199);
  border-radius: 1em;
  margin: -0.2em -0.5em;
  margin-right: 0;
  padding: .2em .5em;
}
.ft-itb .ft-itb-text ~ .ft-itb-value,
.ft-itb .ft-itb-value ~ .ft-itb-text {
  margin-left: .5em;
}
.ft-itb .ft-itb-text ~ .ft-itb-value {
  margin-right: -0.5em;
}



/* DATE & TIME */
table.ft-report-table .ft-date > span,
table.ft-report-table .ft-time > span {
  white-space: nowrap;
}

/* TABLE ASSIGNMENTS */
table.ft-report-table .ft-table-asmt > div {
  margin-top: -0.3em;
  margin-right: -0.3em;
}
table.ft-report-table .ft-table-asmt > div > span {
  display: inline-block;
  margin-top: 0.3em;
  margin-right: 0.3em;
  padding: 0.1em 0.5em;
  border-radius: .8em;
  background: rgba(119, 224, 172, 0.336)
}
table.ft-report-table .ft-table-asmt > div > span + i {
  display: inline-block;
  background: rgba(119, 224, 172, 0);
  border-radius: 0;
  margin-left: 0;
  padding: 0;
}
/*
  Ugly hack to deal with non-reactive (v-once) elements and position: sticky group header positioning.

  Alternative is to iterate each '.ft-group-caption td' of the report in the rendered DOM el
  and set its style="top: ???px" every time this.reportHeaderHeight changes.
  This way is faster for the client to render, but requires this massive class list.
  May yet switch to direct DOM modification on this.reportHeaderHeight change, but for now...
*/
.ft-group-header-10 .ft-group-caption td { top: 10px !important }
.ft-group-header-11 .ft-group-caption td { top: 11px !important }
.ft-group-header-12 .ft-group-caption td { top: 12px !important }
.ft-group-header-13 .ft-group-caption td { top: 13px !important }
.ft-group-header-14 .ft-group-caption td { top: 14px !important }
.ft-group-header-15 .ft-group-caption td { top: 15px !important }
.ft-group-header-16 .ft-group-caption td { top: 16px !important }
.ft-group-header-17 .ft-group-caption td { top: 17px !important }
.ft-group-header-18 .ft-group-caption td { top: 18px !important }
.ft-group-header-19 .ft-group-caption td { top: 19px !important }
.ft-group-header-20 .ft-group-caption td { top: 20px !important }
.ft-group-header-21 .ft-group-caption td { top: 21px !important }
.ft-group-header-22 .ft-group-caption td { top: 22px !important }
.ft-group-header-23 .ft-group-caption td { top: 23px !important }
.ft-group-header-24 .ft-group-caption td { top: 24px !important }
.ft-group-header-25 .ft-group-caption td { top: 25px !important }
.ft-group-header-26 .ft-group-caption td { top: 26px !important }
.ft-group-header-27 .ft-group-caption td { top: 27px !important }
.ft-group-header-28 .ft-group-caption td { top: 28px !important }
.ft-group-header-29 .ft-group-caption td { top: 29px !important }
.ft-group-header-30 .ft-group-caption td { top: 30px !important }
.ft-group-header-31 .ft-group-caption td { top: 31px !important }
.ft-group-header-32 .ft-group-caption td { top: 32px !important }
.ft-group-header-33 .ft-group-caption td { top: 33px !important }
.ft-group-header-34 .ft-group-caption td { top: 34px !important }
.ft-group-header-35 .ft-group-caption td { top: 35px !important }
.ft-group-header-36 .ft-group-caption td { top: 36px !important }
.ft-group-header-37 .ft-group-caption td { top: 37px !important }
.ft-group-header-38 .ft-group-caption td { top: 38px !important }
.ft-group-header-39 .ft-group-caption td { top: 39px !important }
.ft-group-header-40 .ft-group-caption td { top: 40px !important }
.ft-group-header-41 .ft-group-caption td { top: 41px !important }
.ft-group-header-42 .ft-group-caption td { top: 42px !important }
.ft-group-header-43 .ft-group-caption td { top: 43px !important }
.ft-group-header-44 .ft-group-caption td { top: 44px !important }
.ft-group-header-45 .ft-group-caption td { top: 45px !important }
.ft-group-header-46 .ft-group-caption td { top: 46px !important }
.ft-group-header-47 .ft-group-caption td { top: 47px !important }
.ft-group-header-48 .ft-group-caption td { top: 48px !important }
.ft-group-header-49 .ft-group-caption td { top: 49px !important }
.ft-group-header-50 .ft-group-caption td { top: 50px !important }
.ft-group-header-51 .ft-group-caption td { top: 51px !important }
.ft-group-header-52 .ft-group-caption td { top: 52px !important }
.ft-group-header-53 .ft-group-caption td { top: 53px !important }
.ft-group-header-54 .ft-group-caption td { top: 54px !important }
.ft-group-header-55 .ft-group-caption td { top: 55px !important }
.ft-group-header-56 .ft-group-caption td { top: 56px !important }
.ft-group-header-57 .ft-group-caption td { top: 57px !important }
.ft-group-header-58 .ft-group-caption td { top: 58px !important }
.ft-group-header-59 .ft-group-caption td { top: 59px !important }
.ft-group-header-60 .ft-group-caption td { top: 60px !important }
.ft-group-header-61 .ft-group-caption td { top: 61px !important }
.ft-group-header-62 .ft-group-caption td { top: 62px !important }
.ft-group-header-63 .ft-group-caption td { top: 63px !important }
.ft-group-header-64 .ft-group-caption td { top: 64px !important }
.ft-group-header-65 .ft-group-caption td { top: 65px !important }
.ft-group-header-66 .ft-group-caption td { top: 66px !important }
.ft-group-header-67 .ft-group-caption td { top: 67px !important }
.ft-group-header-68 .ft-group-caption td { top: 68px !important }
.ft-group-header-69 .ft-group-caption td { top: 69px !important }
.ft-group-header-70 .ft-group-caption td { top: 70px !important }
.ft-group-header-71 .ft-group-caption td { top: 71px !important }
.ft-group-header-72 .ft-group-caption td { top: 72px !important }
.ft-group-header-73 .ft-group-caption td { top: 73px !important }
.ft-group-header-74 .ft-group-caption td { top: 74px !important }
.ft-group-header-75 .ft-group-caption td { top: 75px !important }
.ft-group-header-76 .ft-group-caption td { top: 76px !important }
.ft-group-header-77 .ft-group-caption td { top: 77px !important }
.ft-group-header-78 .ft-group-caption td { top: 78px !important }
.ft-group-header-79 .ft-group-caption td { top: 79px !important }
.ft-group-header-80 .ft-group-caption td { top: 80px !important }
.ft-group-header-81 .ft-group-caption td { top: 81px !important }
.ft-group-header-82 .ft-group-caption td { top: 82px !important }
.ft-group-header-83 .ft-group-caption td { top: 83px !important }
.ft-group-header-84 .ft-group-caption td { top: 84px !important }
.ft-group-header-85 .ft-group-caption td { top: 85px !important }
.ft-group-header-86 .ft-group-caption td { top: 86px !important }
.ft-group-header-87 .ft-group-caption td { top: 87px !important }
.ft-group-header-88 .ft-group-caption td { top: 88px !important }
.ft-group-header-89 .ft-group-caption td { top: 89px !important }
</style>
